// ════════════════════════════════════════════════════════════
// /depenses/$id — Sprint 3 (vue détail + actions par rôle)
// ════════════════════════════════════════════════════════════
// - Header : code + status + approval + anomaly badges
// - Stepper (DEPENSE_STEPS)
// - Resume business (bénéficiaire, fournisseur, montant, devise, ...)
// - Anomalies inline (liste simple + ack)
// - Imputations + lignes facture (preview)
// - Actions par rôle via computeActions (sticky bar mobile)
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Wallet,
  Banknote,
  ArrowRightLeft,
  FileText,
  Globe,
  ClipboardCheck,
  Send,
  Building2,
  MessageCircle,
  Upload,
  Camera,
  Share2,
  Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useDepense,
  useDepenseAnomalies,
  useDepenseLignes,
  useAckDepenseAnomaly,
  useApproveDepense,
  useRejectDepense,
  usePayDepense,
  useComptabiliserDepense,
  useCancelDepense,
  useConfirmSwiftTransfer,
  useSignedUrl,
  DEPENSE_ANOMALY_TYPE_LABELS,
} from '@/queries/depenses';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { uploadJustificatif } from '@/lib/uploadStorage';
import { shareOrDownloadFile, cleanShareFilename } from '@/lib/shareFile';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { CopyButton } from '@/components/ui/CopyButton';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  APPROVAL_LABELS,
  APPROVAL_COLORS,
  SEVERITY_COLORS,
  DEPENSE_STEPS,
  SEUIL_DOUBLE_VALIDATION_MRU,
  stepIndex,
  computeActions,
  isEnAttenteValidation,
  type Depense,
  type DepenseAnomaly,
} from '@ecr/core/depenses';
import { formatMRU, formatAmount } from '@ecr/core/utils';

// Pour les dépenses payées par virement SWIFT bancaire, `montant` est stocké
// dans la devise étrangère (EUR/USD), pas en MRU (cf. new.tsx : montant =
// montantOrigine). Le coût MRU réel = montant × taux + frais (saisi au SWIFT).
// On affiche donc le montant dans sa vraie devise.
const montantCcy = (devise: string | null | undefined): 'MRU' | 'EUR' | 'USD' =>
  devise === 'EUR' || devise === 'USD' ? devise : 'MRU';

export const Route = createFileRoute('/_authed/depenses/$id')({
  component: DepenseDetailPage,
});

function DepenseDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const userId = session?.user?.id ?? null;
  const userEmail = session?.user?.email ?? '';
  const role = profile?.role ?? null;

  const { data: depense, isLoading, error } = useDepense(id);
  const { data: anomalies = [] } = useDepenseAnomalies({
    depenseId: id,
    includeAcknowledged: true,
  });
  const { data: lignes = [] } = useDepenseLignes(id);
  const { data: comptes = [] } = useComptesTresorerie();

  if (isLoading || !depense) {
    return (
      <div className="space-y-3">
        <div className="bg-muted h-8 w-1/3 animate-pulse rounded" />
        <div className="bg-muted h-24 animate-pulse rounded" />
        <div className="bg-muted h-40 animate-pulse rounded" />
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-6">
          <AlertCircle className="size-5 text-red-600" />
          <p className="text-sm">Erreur : {error instanceof Error ? error.message : 'inconnu'}</p>
        </CardContent>
      </Card>
    );
  }

  const actions = computeActions({
    status: depense.status,
    approvalStatus: depense.approvalStatus,
    modePaiementSouhaite: depense.modePaiementSouhaite,
    role,
    createdByEmail: depense.createdByEmail,
    currentUserEmail: userEmail,
  });

  const isCreator = depense.createdByEmail.toLowerCase() === userEmail.toLowerCase();
  const stepIdx = stepIndex(depense.status);
  const paidCompteLabel = comptes.find((c) => c.id === depense.paidCompteId)?.label ?? null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/depenses">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-foreground font-mono text-xl font-semibold sm:text-2xl">
              {depense.code}
            </h1>
            <CopyButton value={depense.code} label="Code dépense" />
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{
                color: STATUS_COLORS[depense.status].fg,
                backgroundColor: STATUS_COLORS[depense.status].bg,
              }}
            >
              {STATUS_LABELS[depense.status]}
            </span>
            {depense.approvalStatus && (
              <span
                className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                style={{
                  color: APPROVAL_COLORS[depense.approvalStatus].fg,
                  backgroundColor: APPROVAL_COLORS[depense.approvalStatus].bg,
                }}
              >
                {APPROVAL_LABELS[depense.approvalStatus]}
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-1 text-sm">
            {new Intl.DateTimeFormat('fr-FR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }).format(depense.createdAt)}{' '}
            · par {depense.requestedByEmail.split('@')[0]}
          </p>
        </div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(
            `Dépense ${depense.code} · ${depense.beneficiaire} · ${formatAmount(depense.montant, montantCcy(depense.devise))} · statut ${STATUS_LABELS[depense.status]}`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground hidden shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs sm:inline-flex"
          aria-label="Partager sur WhatsApp"
        >
          <MessageCircle className="size-3.5" />
          WhatsApp
        </a>
      </div>

      {/* Double validation « 4 yeux » : en attente / rejetée */}
      <ValidationBanner depense={depense} canDecide={actions.canApprove} isCreator={isCreator} />

      {/* Stepper */}
      {stepIdx >= 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="relative flex items-center justify-between">
              {DEPENSE_STEPS.map((step, i) => {
                const reached = i <= stepIdx;
                const current = i === stepIdx;
                return (
                  <div key={step} className="relative z-10 flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all',
                        reached
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground',
                        current && 'ring-primary/30 scale-110 ring-4',
                      )}
                    >
                      {i + 1}
                    </div>
                    <p
                      className={cn(
                        'text-xs',
                        reached ? 'text-foreground font-medium' : 'text-muted-foreground',
                      )}
                    >
                      {STATUS_LABELS[step]}
                    </p>
                  </div>
                );
              })}
              <div className="bg-border absolute left-4 right-4 top-4 -z-0 h-0.5">
                <div
                  className="bg-primary h-full transition-all"
                  style={{
                    width: `${(stepIdx / (DEPENSE_STEPS.length - 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resume business */}
      <Card>
        <CardContent className="space-y-3 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">Montant</p>
            <p className="text-foreground flex items-center gap-1 font-mono text-2xl font-semibold tabular-nums">
              {formatAmount(depense.montant, montantCcy(depense.devise))}
              <CopyButton value={String(depense.montant)} label="Montant" />
            </p>
          </div>
          {depense.ocrDeviseDetectee && depense.ocrDeviseDetectee !== 'MRU' && (
            <div className="text-muted-foreground flex items-center justify-between text-xs">
              <span className="inline-flex items-center gap-1">
                <Globe className="size-3" />
                Devise origine
              </span>
              <span>
                {depense.ocrMontantOrigine} {depense.ocrDeviseDetectee} (taux{' '}
                {depense.ocrTauxChangeMru})
              </span>
            </div>
          )}
          <div className="border-border border-t pt-3">
            <DetailRow icon={Building2} label="Bénéficiaire" value={depense.beneficiaire} />
            {depense.fournisseurNom && (
              <DetailRow label="Fournisseur" value={depense.fournisseurNom} />
            )}
            {depense.nifFournisseur && (
              <DetailRow label="NIF" value={depense.nifFournisseur} mono />
            )}
            {depense.numFacture && (
              <DetailRow icon={FileText} label="N° facture" value={depense.numFacture} mono />
            )}
            {depense.dateFacture && (
              <DetailRow
                label="Date facture"
                value={new Intl.DateTimeFormat('fr-FR').format(depense.dateFacture)}
              />
            )}
            <DetailRow
              icon={modePaiementIcon(depense.modePaiementSouhaite)}
              label="Mode de paiement"
              value={
                <span className="capitalize">
                  {depense.modePaiementSouhaite}
                  {depense.devise ? ` (${depense.devise})` : ''}
                  {depense.walletType ? ` — ${depense.walletType}` : ''}
                  {paidCompteLabel ? ` — ${paidCompteLabel}` : ''}
                </span>
              }
            />
            {depense.walletNumero && (
              <DetailRow
                icon={Wallet}
                label="Numéro Bankily"
                value={
                  <span className="inline-flex items-center gap-1 font-mono">
                    {depense.walletNumero}
                    <CopyButton value={depense.walletNumero} label="Numéro Bankily" />
                  </span>
                }
              />
            )}
            {depense.categorie && <DetailRow label="Catégorie" value={depense.categorie} />}
            {depense.description && <DetailRow label="Motif" value={depense.description} />}
            {depense.numeroPieceSage && (
              <DetailRow
                icon={ClipboardCheck}
                label="N° pièce Sage"
                value={depense.numeroPieceSage}
                mono
              />
            )}
            {depense.rejectionReason && (
              <DetailRow
                icon={XCircle}
                label="Raison rejet"
                value={
                  <span className="text-red-700 dark:text-red-300">{depense.rejectionReason}</span>
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <Card className="border-orange-200 dark:border-orange-900">
          <CardContent className="space-y-2 py-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-orange-600 dark:text-orange-400" />
              <p className="font-medium text-orange-900 dark:text-orange-100">
                Anomalies détectées ({anomalies.length})
              </p>
            </div>
            <div className="space-y-2">
              {anomalies.map((a) => (
                <AnomalyInline
                  key={a.id}
                  anomaly={a}
                  onAck={() => {
                    /* géré dans le composant */
                  }}
                  currentUserId={userId}
                  currentUserEmail={userEmail}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lignes facture — affichées en devise origine */}
      {lignes.length > 0 && (
        <Card>
          <CardContent className="space-y-2 py-4">
            <p className="text-foreground font-medium">
              Lignes facture en {depense.ocrDeviseDetectee ?? 'MRU'} ({lignes.length})
            </p>
            <div className="space-y-1.5">
              {lignes.map((l) => (
                <div
                  key={l.id}
                  className="border-border flex items-baseline justify-between gap-3 rounded border p-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{l.libelle}</p>
                    {(l.quantite != null || l.unite != null || l.prixUnitaire != null) && (
                      <p className="text-muted-foreground text-xs">
                        {l.quantite ?? '?'} {l.unite ?? ''}
                        {l.prixUnitaire
                          ? ` × ${formatAmount(l.prixUnitaire, depense.ocrDeviseDetectee ?? 'MRU')}`
                          : ''}
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-sm tabular-nums">
                    {l.montantLigne
                      ? formatAmount(l.montantLigne, depense.ocrDeviseDetectee ?? 'MRU')
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique — timeline qui a fait quoi quand (feedback Sidi V1 parity) */}
      <HistoryTimeline depense={depense} paidCompteLabel={paidCompteLabel} />

      {/* Justificatifs — thumbnails cliquables avec signed URLs */}
      {(depense.justificatifsDemandeUrls.length > 0 ||
        depense.justificatifsPaiementUrls.length > 0 ||
        depense.dechargeUrl != null ||
        depense.swiftUrl != null) && (
        <Card>
          <CardContent className="space-y-4 py-4">
            <p className="text-foreground font-medium">Justificatifs</p>
            {depense.justificatifsDemandeUrls.length > 0 && (
              <JustifGroup
                title="Facture (demande)"
                paths={depense.justificatifsDemandeUrls}
                depenseCode={depense.code}
              />
            )}
            {depense.justificatifsPaiementUrls.length > 0 && (
              <JustifGroup
                title="Paiement"
                paths={depense.justificatifsPaiementUrls}
                depenseCode={depense.code}
              />
            )}
            {depense.dechargeUrl && (
              <JustifGroup
                title="Décharge banque"
                paths={[depense.dechargeUrl]}
                depenseCode={depense.code}
              />
            )}
            {depense.swiftUrl && (
              <JustifGroup
                title="SWIFT confirmation"
                paths={[depense.swiftUrl]}
                depenseCode={depense.code}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions sticky bottom bar (mobile-first) */}
      <ActionsBar
        depense={depense}
        actions={actions}
        userId={userId}
        userEmail={userEmail}
        onSuccess={() => {
          // navigate refresh : reste sur la page, queries déjà invalidées
        }}
        onDeleted={() => {
          void navigate({ to: '/depenses' });
        }}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Détail row helper
// ═════════════════════════════════════════════════════════
function DetailRow({
  icon: Icon,
  label,
  value,
  mono,
}: {
  icon?: typeof Building2;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground flex shrink-0 items-center gap-1.5">
        {Icon && <Icon className="size-3.5" />}
        {label}
      </span>
      <span className={cn('text-foreground text-right', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

function modePaiementIcon(mode: 'wallet' | 'cheque' | 'transfer') {
  if (mode === 'wallet') return Wallet;
  if (mode === 'cheque') return Banknote;
  return ArrowRightLeft;
}

function JustifGroup({
  title,
  paths,
  depenseCode,
}: {
  title: string;
  paths: string[];
  depenseCode: string;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs font-medium uppercase">{title}</p>
      <div className="flex flex-wrap gap-2">
        {paths.map((path, i) => (
          <JustifThumb
            key={`${path}-${i}`}
            path={path}
            shareFilename={cleanShareFilename(title, depenseCode, path)}
            shareText={`${title} — dépense ${depenseCode}`}
          />
        ))}
      </div>
    </div>
  );
}

function JustifThumb({
  path,
  shareFilename,
  shareText,
}: {
  path: string;
  shareFilename: string;
  shareText: string;
}) {
  const { data: signedUrl, isLoading, error } = useSignedUrl(path);
  const [sharing, setSharing] = useState(false);
  const isImage = /\.(jpe?g|png|webp|gif)(\?|$)/i.test(path);
  const isPdf = /\.pdf(\?|$)/i.test(path);
  const filename = path.split('/').pop() ?? 'fichier';

  async function handleShare() {
    if (!signedUrl) return;
    setSharing(true);
    try {
      const outcome = await shareOrDownloadFile({
        signedUrl,
        filename: shareFilename,
        text: shareText,
      });
      if (outcome === 'downloaded') {
        toast.success(`Fichier téléchargé (${shareFilename}) — joins-le à ton message.`);
      }
    } catch (e) {
      toast.error(`Partage échoué : ${e instanceof Error ? e.message : 'inconnu'}`);
    } finally {
      setSharing(false);
    }
  }

  if (isLoading) {
    return (
      <div className="border-border bg-muted/30 flex size-24 items-center justify-center rounded border">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }
  if (error || !signedUrl) {
    return (
      <div className="border-border bg-muted/30 flex size-24 flex-col items-center justify-center rounded border p-1 text-center">
        <AlertCircle className="text-muted-foreground size-4" />
        <p className="text-muted-foreground text-[10px]">URL expirée</p>
      </div>
    );
  }

  return (
    <div className="w-24 space-y-1">
      <a
        href={signedUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={filename}
        className="border-border hover:border-primary group relative block size-24 overflow-hidden rounded border transition-colors"
      >
        {isImage ? (
          <img src={signedUrl} alt={filename} className="size-full object-cover" loading="lazy" />
        ) : isPdf ? (
          <div className="flex size-full flex-col items-center justify-center gap-1 bg-red-50 p-1 text-center dark:bg-red-950/40">
            <FileText className="size-7 text-red-600 dark:text-red-400" />
            <span className="text-muted-foreground truncate text-[10px]">PDF</span>
          </div>
        ) : (
          <div className="bg-muted/40 flex size-full flex-col items-center justify-center gap-1 p-1 text-center">
            <FileText className="text-muted-foreground size-7" />
            <span className="text-muted-foreground truncate text-[10px]">{filename}</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
          Ouvrir
        </div>
      </a>
      {/* Partage du FICHIER (pas du lien signé, énorme et périssable) :
          mobile → feuille de partage WhatsApp ; desktop → téléchargement. */}
      <button
        type="button"
        onClick={() => void handleShare()}
        disabled={sharing}
        className="border-input hover:bg-accent text-foreground inline-flex h-7 w-full items-center justify-center gap-1 rounded-md border text-[11px] font-medium transition-colors disabled:opacity-50"
      >
        {sharing ? <Loader2 className="size-3 animate-spin" /> : <Share2 className="size-3" />}
        Partager
      </button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Historique timeline (qui a fait quoi quand)
// ═════════════════════════════════════════════════════════

interface TimelineEvent {
  at: Date;
  icon: typeof CheckCircle2;
  iconColor: string;
  title: string;
  who: string | null;
  details?: string;
}

function HistoryTimeline({
  depense,
  paidCompteLabel,
}: {
  depense: Depense;
  paidCompteLabel: string | null;
}) {
  const events: TimelineEvent[] = [];

  events.push({
    at: depense.requestedAt,
    icon: Send,
    iconColor: 'text-blue-600 dark:text-blue-400',
    title: 'Dépense demandée',
    who: shortEmail(depense.requestedByEmail),
    details: depense.requestedByName ?? undefined,
  });

  if (depense.approvalAt) {
    events.push({
      at: depense.approvalAt,
      icon: depense.approvalStatus === 'approved' ? CheckCircle2 : XCircle,
      iconColor:
        depense.approvalStatus === 'approved'
          ? 'text-green-600 dark:text-green-400'
          : 'text-red-600 dark:text-red-400',
      title: depense.approvalStatus === 'approved' ? 'Validée' : 'Rejetée',
      who: depense.approvalByEmail ? shortEmail(depense.approvalByEmail) : null,
      details: depense.rejectionReason ?? undefined,
    });
  }

  if (depense.paidAt) {
    events.push({
      at: depense.paidAt,
      icon: Send,
      iconColor: 'text-purple-600 dark:text-purple-400',
      title:
        depense.modePaiementSouhaite === 'transfer'
          ? 'Transfert SWIFT initié'
          : `Payée (${depense.modePaiementSouhaite})`,
      who: depense.paidByEmail ? shortEmail(depense.paidByEmail) : null,
      details: paidCompteLabel ?? undefined,
    });
  }

  if (depense.swiftUploadedAt) {
    events.push({
      at: depense.swiftUploadedAt,
      icon: CheckCircle2,
      iconColor: 'text-green-600 dark:text-green-400',
      title: 'SWIFT confirmé',
      who: depense.swiftUploadedByEmail ? shortEmail(depense.swiftUploadedByEmail) : null,
    });
  }

  if (depense.comptabiliseAt) {
    events.push({
      at: depense.comptabiliseAt,
      icon: ClipboardCheck,
      iconColor: 'text-green-700 dark:text-green-300',
      title: `Comptabilisée${depense.numeroPieceSage ? ` (${depense.numeroPieceSage})` : ''}`,
      who: depense.comptabiliseByEmail ? shortEmail(depense.comptabiliseByEmail) : null,
    });
  }

  if (depense.status === 'annulée') {
    events.push({
      at: depense.modifiedAt ?? depense.createdAt,
      icon: XCircle,
      iconColor: 'text-gray-500 dark:text-gray-400',
      title: 'Annulée',
      who: null,
    });
  }

  // Trie par date (au cas où)
  events.sort((a, b) => a.at.getTime() - b.at.getTime());

  if (events.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <p className="text-foreground font-medium">Historique</p>
        <ol className="space-y-3">
          {events.map((e, i) => {
            const Icon = e.icon;
            return (
              <li key={i} className="flex items-start gap-3">
                <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
                  <Icon className={cn('size-4', e.iconColor)} />
                </div>
                <div className="flex-1">
                  <p className="text-foreground text-sm font-medium">{e.title}</p>
                  <p className="text-muted-foreground text-xs">
                    {new Intl.DateTimeFormat('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(e.at)}
                    {e.who && ` · par ${e.who}`}
                  </p>
                  {e.details && (
                    <p className="text-muted-foreground mt-0.5 text-xs italic">{e.details}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function shortEmail(email: string): string {
  return email.split('@')[0] ?? email;
}

// ═════════════════════════════════════════════════════════
// Anomalie inline (liste simple + ack)
// ═════════════════════════════════════════════════════════

function AnomalyInline({
  anomaly,
  currentUserId,
  currentUserEmail,
}: {
  anomaly: DepenseAnomaly;
  onAck: () => void;
  currentUserId: string | null;
  currentUserEmail: string;
}) {
  const ackMutation = useAckDepenseAnomaly();
  const colors = SEVERITY_COLORS[anomaly.severity];

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-opacity',
        anomaly.acknowledgedAt && 'opacity-60',
      )}
      style={{ borderColor: colors.bg }}
    >
      <div className="flex items-start gap-2">
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ color: colors.fg, backgroundColor: colors.bg }}
        >
          {DEPENSE_ANOMALY_TYPE_LABELS[anomaly.type] ?? anomaly.type}
        </span>
        <p className="text-foreground flex-1 text-sm">{anomaly.message}</p>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          {anomaly.acknowledgedAt
            ? `Traitée par ${anomaly.acknowledgedByEmail?.split('@')[0] ?? 'inconnu'}`
            : new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              }).format(anomaly.detectedAt)}
        </span>
        {!anomaly.acknowledgedAt && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={ackMutation.isPending}
            onClick={() => {
              ackMutation.mutate(
                {
                  anomalyId: anomaly.id,
                  userId: currentUserId,
                  userEmail: currentUserEmail,
                },
                {
                  onSuccess: () => toast.success('Anomalie traitée'),
                  onError: (e) =>
                    toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
                },
              );
            }}
          >
            <CheckCircle2 className="size-3" />
            Marquer traité
          </Button>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Bandeau double validation « 4 yeux » (> 100 000 MRU ou SWIFT)
// ═════════════════════════════════════════════════════════

function ValidationBanner({
  depense,
  canDecide,
  isCreator,
}: {
  depense: Depense;
  /** L'utilisateur courant est le DAF dont la validation est attendue. */
  canDecide: boolean;
  isCreator: boolean;
}) {
  const regle =
    depense.modePaiementSouhaite === 'transfer'
      ? 'transfert SWIFT'
      : `montant supérieur à ${formatMRU(SEUIL_DOUBLE_VALIDATION_MRU)}`;
  const demandeur = shortEmail(depense.createdByEmail);

  if (isEnAttenteValidation(depense)) {
    return (
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
        <CardContent className="flex items-start gap-3 py-3">
          <CheckCircle2 className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex-1 space-y-0.5 text-sm">
            <p className="font-medium text-amber-900 dark:text-amber-100">
              {canDecide
                ? 'Ta validation est requise'
                : isCreator
                  ? "En attente de validation par l'autre DAF"
                  : 'En attente de validation par le second DAF'}
            </p>
            <p className="text-amber-800 dark:text-amber-200">
              Règle des 4 yeux ({regle}) : demandée par <strong>{demandeur}</strong>
              {canDecide
                ? '. Elle ne pourra être payée qu’après ta validation — valide ou rejette ci-dessous.'
                : isCreator
                  ? ' — tu ne peux pas valider ta propre demande.'
                  : ' — le paiement est bloqué jusqu’à validation.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (depense.approvalStatus === 'rejected') {
    return (
      <Card className="border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30">
        <CardContent className="flex items-start gap-3 py-3">
          <XCircle className="size-5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="flex-1 space-y-0.5 text-sm">
            <p className="font-medium text-red-900 dark:text-red-100">
              Rejetée par le second DAF — dépense annulée
            </p>
            {depense.rejectionReason && (
              <p className="text-red-800 dark:text-red-200">
                <span className="font-medium">Motif :</span> {depense.rejectionReason}
              </p>
            )}
            {depense.approvalByEmail && depense.approvalAt && (
              <p className="text-xs text-red-700 dark:text-red-300">
                Par {shortEmail(depense.approvalByEmail)} le{' '}
                {new Intl.DateTimeFormat('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                }).format(depense.approvalAt)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}

// ═════════════════════════════════════════════════════════
// Actions bar (sticky bottom mobile, inline desktop)
// ═════════════════════════════════════════════════════════

type ActionFlags = ReturnType<typeof computeActions>;
type ActionMode = 'pay' | 'approve' | 'reject' | 'comptabiliser' | 'confirmSwift' | 'cancel' | null;

function ActionsBar({
  depense,
  actions,
  userId,
  userEmail,
  onSuccess,
  onDeleted,
}: {
  depense: Depense;
  actions: ActionFlags;
  userId: string | null;
  userEmail: string;
  onSuccess: () => void;
  onDeleted: () => void;
}) {
  const [mode, setMode] = useState<ActionMode>(null);

  const hasAny =
    actions.canApprove ||
    actions.canReject ||
    actions.canPay ||
    actions.canComptabiliser ||
    actions.canCancel ||
    actions.canUploadSwiftDocs;

  if (!hasAny) return null;

  return (
    <>
      <div className="bg-background border-border fixed inset-x-0 bottom-16 z-40 border-t p-3 pb-[env(safe-area-inset-bottom,0)] sm:relative sm:bottom-0 sm:rounded-lg sm:border sm:p-4">
        <div className="mx-auto flex max-w-3xl flex-wrap gap-2">
          {actions.canApprove && (
            <Button
              type="button"
              onClick={() => setMode('approve')}
              className="flex-1 gap-2 bg-green-600 hover:bg-green-700 sm:flex-initial"
            >
              <CheckCircle2 className="size-4" />
              Valider
            </Button>
          )}
          {actions.canReject && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setMode('reject')}
              className="flex-1 gap-2 sm:flex-initial"
            >
              <XCircle className="size-4" />
              Rejeter
            </Button>
          )}
          {actions.canPay && (
            <Button
              type="button"
              onClick={() => setMode('pay')}
              className="flex-1 gap-2 sm:flex-initial"
            >
              <Send className="size-4" />
              Payer
            </Button>
          )}
          {actions.canUploadSwiftDocs && depense.status === 'en_cours_transfert' && (
            <Button
              type="button"
              onClick={() => setMode('confirmSwift')}
              className="flex-1 gap-2 sm:flex-initial"
            >
              <CheckCircle2 className="size-4" />
              Confirmer SWIFT
            </Button>
          )}
          {actions.canComptabiliser && (
            <Button
              type="button"
              onClick={() => setMode('comptabiliser')}
              className="flex-1 gap-2 sm:flex-initial"
            >
              <ClipboardCheck className="size-4" />
              Comptabiliser
            </Button>
          )}
          {actions.canCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setMode('cancel')}
              className="gap-2"
            >
              <XCircle className="size-4" />
              Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Modal/sheet actions */}
      {mode && (
        <ActionSheet
          depense={depense}
          mode={mode}
          userId={userId}
          userEmail={userEmail}
          onClose={() => setMode(null)}
          onSuccess={() => {
            setMode(null);
            onSuccess();
          }}
          onDeleted={() => {
            setMode(null);
            onDeleted();
          }}
        />
      )}
    </>
  );
}

function ActionSheet({
  depense,
  mode,
  userId,
  userEmail,
  onClose,
  onSuccess,
  onDeleted,
}: {
  depense: Depense;
  mode: Exclude<ActionMode, null>;
  userId: string | null;
  userEmail: string;
  onClose: () => void;
  onSuccess: () => void;
  onDeleted: () => void;
}) {
  const approve = useApproveDepense();
  const reject = useRejectDepense();
  const pay = usePayDepense();
  const compta = useComptabiliserDepense();
  const cancel = useCancelDepense();
  const confirmSwift = useConfirmSwiftTransfer();
  const { data: comptes = [] } = useComptesTresorerie();
  const paidCompteLabel = comptes.find((c) => c.id === depense.paidCompteId)?.label ?? null;

  const [rejectReason, setRejectReason] = useState('');
  const [numeroPieceSage, setNumeroPieceSage] = useState('');
  // Saisie taux + frais SWIFT au moment de l'upload (feedback Sidi 2026-05-16)
  const [swiftTaux, setSwiftTaux] = useState('');
  const [swiftFrais, setSwiftFrais] = useState('');
  // Upload justificatifs paiement (Sprint 3 patch Fatimetou 2026-05-18)
  const [payJustifs, setPayJustifs] = useState<{ path: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  // Pay mode SWIFT : décharge banque uploadée à l'étape "Payer" (pas à confirmSwift)
  const [payDechargeFile, setPayDechargeFile] = useState<{ path: string; name: string } | null>(
    null,
  );
  const [payDechargeUploading, setPayDechargeUploading] = useState(false);
  // confirmSwift : juste le SWIFT bordereau (la décharge est déjà uploadée)
  const [swiftConfirmFile, setSwiftConfirmFile] = useState<{ path: string; name: string } | null>(
    null,
  );
  const [swiftUploadingConfirm, setSwiftUploadingConfirm] = useState(false);

  async function handlePayDechargeFileSelected(file: File): Promise<void> {
    if (file.size > 10_000_000) {
      toast.error('Fichier trop lourd (max 10 MB)');
      return;
    }
    setPayDechargeUploading(true);
    try {
      const { path } = await uploadJustificatif({
        tenantId: depense.tenantId,
        depenseId: depense.id,
        kind: 'decharge',
        file,
      });
      setPayDechargeFile({ path, name: file.name });
      toast.success('Décharge banque uploadée');
    } catch (err) {
      toast.error(`Upload échoué : ${err instanceof Error ? err.message : 'inconnu'}`);
    } finally {
      setPayDechargeUploading(false);
    }
  }

  async function handleSwiftConfirmFileSelected(file: File): Promise<void> {
    if (file.size > 10_000_000) {
      toast.error('Fichier trop lourd (max 10 MB)');
      return;
    }
    setSwiftUploadingConfirm(true);
    try {
      const { path } = await uploadJustificatif({
        tenantId: depense.tenantId,
        depenseId: depense.id,
        kind: 'swift',
        file,
      });
      setSwiftConfirmFile({ path, name: file.name });
      toast.success('SWIFT uploadé');
    } catch (err) {
      toast.error(`Upload échoué : ${err instanceof Error ? err.message : 'inconnu'}`);
    } finally {
      setSwiftUploadingConfirm(false);
    }
  }

  async function handlePayFileSelected(file: File) {
    if (file.size > 10_000_000) {
      toast.error('Fichier trop lourd (max 10 MB)');
      return;
    }
    setUploading(true);
    try {
      // Upload Supabase Storage
      const { path } = await uploadJustificatif({
        tenantId: depense.tenantId,
        depenseId: depense.id,
        kind: 'paiement',
        file,
      });
      setPayJustifs((p) => [...p, { path, name: file.name }]);
      toast.success(`Justificatif uploadé : ${file.name}`);
    } catch (err) {
      toast.error(`Upload échoué : ${err instanceof Error ? err.message : 'inconnu'}`);
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit() {
    if (mode === 'approve') {
      approve.mutate(
        { depenseId: depense.id, approvalByUserId: userId, approvalByEmail: userEmail },
        {
          onSuccess: () => {
            toast.success('Dépense validée — elle peut être payée');
            onSuccess();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
    if (mode === 'reject') {
      if (!rejectReason.trim()) {
        toast.error('Raison du rejet obligatoire');
        return;
      }
      reject.mutate(
        {
          depenseId: depense.id,
          approvalByUserId: userId,
          approvalByEmail: userEmail,
          rejectionReason: rejectReason.trim(),
        },
        {
          onSuccess: () => {
            toast.success('Dépense rejetée et annulée');
            onSuccess();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
    if (mode === 'pay') {
      // Mode wallet/cheque : justificatif paiement obligatoire
      if (depense.modePaiementSouhaite !== 'transfer' && payJustifs.length === 0) {
        toast.error('Upload au moins 1 justificatif paiement avant de confirmer');
        return;
      }
      // Mode SWIFT : décharge banque obligatoire pour passer en en_cours_transfert
      if (depense.modePaiementSouhaite === 'transfer' && !payDechargeFile) {
        toast.error('Upload la décharge banque (preuve de dépôt SWIFT) avant de confirmer');
        return;
      }
      pay.mutate(
        {
          depenseId: depense.id,
          modePaiementSouhaite: depense.modePaiementSouhaite,
          paidByUserId: userId,
          paidByEmail: userEmail,
          paidCompteId: depense.paidCompteId ?? undefined,
          walletType: depense.walletType ?? undefined,
          walletNumero: depense.walletNumero ?? undefined,
          devise:
            depense.modePaiementSouhaite === 'transfer'
              ? ((depense.devise as 'EUR' | 'USD' | null) ?? undefined)
              : undefined,
          justificatifsPaiementUrls: payJustifs.map((j) => j.path),
          dechargeUrl: payDechargeFile?.path,
        },
        {
          onSuccess: () => {
            toast.success(
              depense.modePaiementSouhaite === 'transfer'
                ? 'Transfert SWIFT en cours (décharge banque uploadée). Confirme à la réception du SWIFT bordereau.'
                : 'Paiement enregistré',
            );
            onSuccess();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
    if (mode === 'confirmSwift') {
      const tauxNum = Number(swiftTaux.replace(',', '.')) || 0;
      const fraisNum = Number(swiftFrais.replace(',', '.')) || 0;
      if (tauxNum <= 0) {
        toast.error('Taux MRU/devise effectif obligatoire');
        return;
      }
      if (!swiftConfirmFile) {
        toast.error('Upload le SWIFT bordereau');
        return;
      }
      confirmSwift.mutate(
        {
          depenseId: depense.id,
          swiftUrl: swiftConfirmFile.path,
          uploadedByEmail: userEmail,
          swiftTauxChange: tauxNum,
          swiftFraisBancaires: fraisNum > 0 ? fraisNum : undefined,
        },
        {
          onSuccess: () => {
            toast.success(
              `SWIFT confirmé : taux ${tauxNum}${fraisNum > 0 ? ` + ${fraisNum} MRU frais` : ''}`,
            );
            onSuccess();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
    if (mode === 'comptabiliser') {
      if (!numeroPieceSage.trim()) {
        toast.error('N° pièce Sage obligatoire');
        return;
      }
      compta.mutate(
        {
          depenseId: depense.id,
          numeroPieceSage: numeroPieceSage.trim(),
          comptabiliseByUserId: userId,
          comptabiliseByEmail: userEmail,
        },
        {
          onSuccess: () => {
            toast.success('Dépense comptabilisée');
            onSuccess();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
    if (mode === 'cancel') {
      cancel.mutate(
        { depenseId: depense.id },
        {
          onSuccess: () => {
            toast.success('Dépense annulée');
            onDeleted();
          },
          onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
        },
      );
    }
  }

  const titles: Record<typeof mode, string> = {
    approve: 'Valider la dépense',
    reject: 'Rejeter la dépense',
    pay: 'Confirmer le paiement',
    comptabiliser: 'Comptabiliser dans Sage',
    confirmSwift: 'Confirmer le transfert SWIFT',
    cancel: 'Annuler la dépense',
  };

  const isPending =
    approve.isPending ||
    reject.isPending ||
    pay.isPending ||
    compta.isPending ||
    cancel.isPending ||
    confirmSwift.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-md rounded-t-xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-foreground text-lg font-semibold">{titles[mode]}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Fermer"
            >
              <XCircle className="size-5" />
            </button>
          </div>

          <p className="text-muted-foreground text-sm">
            <span className="text-foreground font-mono font-medium">{depense.code}</span> ·{' '}
            {formatMRU(depense.montant)} · {depense.beneficiaire}
          </p>

          {/* Motif : le comptable doit voir p.ex. « À imputer sur le compte X »
              avant de saisir la pièce Sage — sans rouvrir le détail. */}
          {depense.description && (
            <p className="bg-muted/50 text-foreground rounded p-3 text-sm">
              <span className="text-muted-foreground">Motif : </span>
              {depense.description}
            </p>
          )}

          {mode === 'approve' && (
            <p className="text-sm">
              Règle des 4 yeux : dépense demandée par{' '}
              <strong>{shortEmail(depense.createdByEmail)}</strong>. En la validant, tu autorises
              son paiement par la responsable financière.
            </p>
          )}

          {mode === 'reject' && (
            <div className="space-y-1.5">
              <Label>Raison du rejet</Label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="border-input bg-background flex w-full rounded-md border px-3 py-2 text-sm"
                placeholder="Expliquer pourquoi (sera visible par le demandeur)"
                autoFocus
              />
              <p className="text-muted-foreground text-xs">
                La dépense passera en <strong>annulée</strong> avec ce motif. Pour corriger, le
                demandeur refera une nouvelle demande.
              </p>
            </div>
          )}

          {mode === 'pay' && (
            <div className="space-y-3">
              {/* Détails paiement copiables (workflow Fatimetou : copier-coller
                  le numéro et le montant directement dans l'app de paiement) */}
              <div className="bg-muted/50 space-y-2 rounded p-3 text-sm">
                <p className="text-foreground capitalize">
                  Mode : <span className="font-medium">{depense.modePaiementSouhaite}</span>
                  {depense.walletType && ` — ${depense.walletType}`}
                  {paidCompteLabel && ` — ${paidCompteLabel}`}
                </p>
                {depense.walletNumero && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Numéro Bankily</span>
                    <span className="text-foreground inline-flex items-center gap-1 font-mono font-semibold">
                      {depense.walletNumero}
                      <CopyButton value={depense.walletNumero} label="Numéro Bankily" />
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Montant à payer</span>
                  <span className="text-foreground inline-flex items-center gap-1 font-mono font-semibold">
                    {formatAmount(depense.montant, montantCcy(depense.devise))}
                    <CopyButton value={String(depense.montant)} label="Montant" />
                  </span>
                </div>
              </div>

              {/* Upload justificatifs (obligatoire pour wallet/cheque) */}
              {depense.modePaiementSouhaite !== 'transfer' && (
                <div className="space-y-2">
                  <Label className="after:text-red-500 after:content-['*']">
                    {depense.modePaiementSouhaite === 'wallet'
                      ? 'Bordereau wallet'
                      : 'Photo du chèque'}
                  </Label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium">
                      <Camera className="size-4" />
                      Prendre une photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handlePayFileSelected(f);
                          e.target.value = ''; // reset pour pouvoir re-upload même fichier
                        }}
                      />
                    </label>
                    <label className="border-input hover:bg-accent inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium">
                      <Upload className="size-4" />
                      Fichier
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handlePayFileSelected(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  {uploading && (
                    <p className="text-muted-foreground flex items-center gap-2 text-xs">
                      <Loader2 className="size-3 animate-spin" />
                      Upload Supabase Storage…
                    </p>
                  )}

                  {/* Liste justifs uploadés */}
                  {payJustifs.length > 0 && (
                    <div className="space-y-1.5">
                      {payJustifs.map((j, idx) => (
                        <div
                          key={idx}
                          className="border-border flex items-center gap-2 rounded border p-2 text-sm"
                        >
                          <ImageIcon className="size-4 shrink-0 text-green-600 dark:text-green-400" />
                          <span className="flex-1 truncate">{j.name}</span>
                          <button
                            type="button"
                            onClick={() => setPayJustifs((p) => p.filter((_, i) => i !== idx))}
                            className="text-muted-foreground text-xs hover:text-red-600"
                            aria-label="Supprimer"
                          >
                            <XCircle className="size-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Mode SWIFT : upload décharge banque obligatoire (étape 1) */}
              {depense.modePaiementSouhaite === 'transfer' && (
                <div className="space-y-2">
                  <Label className="after:text-red-500 after:content-['*']">
                    Décharge banque (preuve dépôt SWIFT)
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Upload la décharge que la banque te remet au moment du dépôt de l&apos;ordre. Le
                    SWIFT confirmé arrivera plus tard (utilise alors &quot;Confirmer SWIFT&quot;).
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium">
                      <Camera className="size-4" />
                      Prendre une photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        disabled={payDechargeUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handlePayDechargeFileSelected(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <label className="border-input hover:bg-accent inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium">
                      <Upload className="size-4" />
                      Fichier
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                        disabled={payDechargeUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handlePayDechargeFileSelected(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  {payDechargeUploading && (
                    <p className="text-muted-foreground flex items-center gap-2 text-xs">
                      <Loader2 className="size-3 animate-spin" />
                      Upload décharge…
                    </p>
                  )}
                  {payDechargeFile && (
                    <div className="border-border flex items-center gap-2 rounded border p-2 text-sm">
                      <ImageIcon className="size-4 shrink-0 text-green-600 dark:text-green-400" />
                      <span className="flex-1 truncate">{payDechargeFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setPayDechargeFile(null)}
                        className="text-muted-foreground hover:text-red-600"
                        aria-label="Supprimer"
                      >
                        <XCircle className="size-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {mode === 'comptabiliser' && (
            <div className="space-y-1.5">
              <Label>N° pièce Sage</Label>
              <Input
                value={numeroPieceSage}
                onChange={(e) => setNumeroPieceSage(e.target.value)}
                placeholder="ex. PIECE-2026-0042"
                autoFocus
              />
            </div>
          )}

          {mode === 'confirmSwift' && (
            <div className="space-y-3">
              <div className="bg-muted/50 rounded p-3 text-sm">
                <p className="text-foreground">
                  Montant facture :{' '}
                  <span className="font-mono font-semibold">
                    {formatAmount(depense.montant, montantCcy(depense.devise))}
                  </span>
                </p>
                <p className="text-muted-foreground text-xs">
                  Saisis maintenant le taux effectif appliqué par la banque.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="after:text-red-500 after:content-['*']">
                  Taux MRU pour 1 {depense.devise ?? 'EUR'}
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={swiftTaux}
                  onChange={(e) => setSwiftTaux(e.target.value)}
                  placeholder="ex. 44.85"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label>Frais bancaires MRU (optionnel)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={swiftFrais}
                  onChange={(e) => setSwiftFrais(e.target.value)}
                  placeholder="ex. 5000"
                />
              </div>
              {Number(swiftTaux.replace(',', '.')) > 0 && (
                <div className="rounded bg-blue-50 p-2 text-xs text-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
                  Total débité estimé :{' '}
                  <span className="font-mono font-semibold">
                    {(
                      depense.montant * Number(swiftTaux.replace(',', '.')) +
                      (Number(swiftFrais.replace(',', '.')) || 0)
                    ).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}{' '}
                    MRU
                  </span>
                </div>
              )}
              {/* Upload SWIFT confirmation */}
              <div className="space-y-1.5">
                <Label className="after:text-red-500 after:content-['*']">
                  SWIFT bordereau confirmé (PDF banque)
                </Label>
                <p className="text-muted-foreground text-xs">
                  La décharge banque est déjà attachée (étape Payer). Upload maintenant le SWIFT
                  confirmé que la banque te renvoie après exécution.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <label className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium">
                    <Camera className="size-4" />
                    Photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      disabled={swiftUploadingConfirm}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleSwiftConfirmFileSelected(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <label className="border-input hover:bg-accent inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium">
                    <Upload className="size-4" />
                    Fichier
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      disabled={swiftUploadingConfirm}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleSwiftConfirmFileSelected(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {swiftUploadingConfirm && (
                  <p className="text-muted-foreground flex items-center gap-2 text-xs">
                    <Loader2 className="size-3 animate-spin" />
                    Upload SWIFT…
                  </p>
                )}
                {swiftConfirmFile && (
                  <div className="border-border flex items-center gap-2 rounded border p-2 text-sm">
                    <ImageIcon className="size-4 shrink-0 text-green-600 dark:text-green-400" />
                    <span className="flex-1 truncate">{swiftConfirmFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSwiftConfirmFile(null);
                      }}
                      className="text-muted-foreground hover:text-red-600"
                      aria-label="Supprimer"
                    >
                      <XCircle className="size-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'cancel' && (
            <p className="text-sm">
              Cette dépense passera en statut <strong>annulée</strong> et ne sera plus modifiable.
              Cette action ne peut pas être annulée.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              variant={mode === 'reject' || mode === 'cancel' ? 'destructive' : 'default'}
              className="gap-2"
            >
              {isPending && <Loader2 className="size-4 animate-spin" />}
              Confirmer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
